import React, { useEffect, useRef, useState } from "react";
import gsap from "gsap";

const Cursor = () => {
  const cursorRef = useRef(null);
  const rippleContainerRef = useRef(null);
  const [isInTeamSection, setIsInTeamSection] = useState(false);
  const [isHoveringClickable, setIsHoveringClickable] = useState(false);

  // Check if on mobile/touch device - use state to avoid hooks violation
  const [isMobile, setIsMobile] = useState(false);

  // Check for mobile on mount
  useEffect(() => {
    const checkMobile = () => {
      const mobile =
        window.innerWidth <= 900 ||
        "ontouchstart" in window ||
        navigator.maxTouchPoints > 0;
      setIsMobile(mobile);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    // Don't add listeners if on mobile
    if (isMobile) return;

    // Clickable elements selectors - comprehensive list for entire site
    const clickableSelectors = [
      // Standard interactive elements
      "a",
      "button",
      "input",
      "textarea",
      "select",
      "[role='button']",
      "[data-clickable]",

      // Navigation
      ".nav-links a",
      ".sidebar a",
      ".logo",

      // Images and media
      "img",
      "video",
      ".work-card-media",
      ".work-card-media-container",
      ".member-image-wrapper",
      ".member-image-right",

      // Buttons and links
      ".btn-text",
      ".back-button",
      ".work-card-button",
      ".work-card-arrow",

      // Work section - only images and folder tabs (for going back)
      ".work-card-media",
      ".work-card-media-container",
      ".work-card-folder-tab",

      // Other cards
      ".matte-paper-card",

      // Form elements
      ".contact-form input",
      ".contact-form textarea",
      ".contact-form button",

      // Contact section links
      ".stack-list a",

      // Interactive headlines
      "h1 span",
    ].join(", ");

    // 1. Setup the movement function + detect if in team section + detect clickable
    const moveCursor = (e) => {
      gsap.to(cursorRef.current, {
        x: e.clientX,
        y: e.clientY,
        duration: 0.1,
        ease: "power2.out",
      });

      // Check if mouse is inside team section
      const teamSection = document.getElementById("team");
      if (teamSection) {
        const rect = teamSection.getBoundingClientRect();
        const isInside =
          e.clientX >= rect.left &&
          e.clientX <= rect.right &&
          e.clientY >= rect.top &&
          e.clientY <= rect.bottom;
        setIsInTeamSection(isInside);
      }

      // Check if hovering over clickable element
      const hoveredElement = document.elementFromPoint(e.clientX, e.clientY);
      if (hoveredElement) {
        const isClickable = hoveredElement.closest(clickableSelectors);
        setIsHoveringClickable(!!isClickable);
      } else {
        setIsHoveringClickable(false);
      }
    };

    // 2. Ripple effect on click
    const createRipple = (e) => {
      // Don't create ripple if in team section
      const teamSection = document.getElementById("team");
      if (teamSection) {
        const rect = teamSection.getBoundingClientRect();
        const isInside =
          e.clientX >= rect.left &&
          e.clientX <= rect.right &&
          e.clientY >= rect.top &&
          e.clientY <= rect.bottom;
        if (isInside) return;
      }

      // Create ripple element
      const ripple = document.createElement("div");
      ripple.className = "cursor-ripple";
      ripple.style.left = `${e.clientX}px`;
      ripple.style.top = `${e.clientY}px`;

      rippleContainerRef.current?.appendChild(ripple);

      // Animate ripple
      gsap.fromTo(
        ripple,
        {
          scale: 0,
          opacity: 0.9,
        },
        {
          scale: 4,
          opacity: 0,
          duration: 0.8,
          ease: "power2.out",
          onComplete: () => {
            ripple.remove();
          },
        }
      );
    };

    // 3. Add Event Listeners
    window.addEventListener("mousemove", moveCursor);
    window.addEventListener("click", createRipple);

    // 4. Cleanup
    return () => {
      window.removeEventListener("mousemove", moveCursor);
      window.removeEventListener("click", createRipple);
    };
  }, [isMobile]);

  // Don't render anything on mobile
  if (isMobile) {
    return null;
  }

  return (
    <>
      {/* Ripple container */}
      <div ref={rippleContainerRef} className="cursor-ripple-container" />

      {/* Main cursor */}
      <div
        ref={cursorRef}
        className={`orange-cursor ${
          isHoveringClickable ? "cursor-clickable" : ""
        }`}
        style={{
          opacity: isInTeamSection ? 0 : 1,
          visibility: isInTeamSection ? "hidden" : "visible",
        }}
      />
    </>
  );
};

export default Cursor;
